'use strict';

// ============================================================================
// TaskHound frontend boot
// Kept intentionally minimal: theme bootstrapping + the THLive SSE helper.
// Page-specific behavior belongs in page modules, not here.
// ============================================================================

// ----------------------------------------------------------------------------
// Theme boot — dark ("Hound") is the default and the brand. A stored
// preference wins, but ONLY inside the dashboard app (body[data-app=
// "dashboard"], set by pages/templates/dashboard/app.twig): marketing and
// gateway pages always render the brand theme regardless of what a user
// picked in the dashboard. Tokens live in resources/scss/global/_theme.scss,
// per-theme overrides in global/_themes.scss.
// ----------------------------------------------------------------------------
const THEME_KEY = 'th-theme';

// Theme registry — names must match the [data-theme="…"] blocks in
// global/_themes.scss; swatch = [page bg, accent, text] hexes shown in the
// picker (mirrored statically in dashboard/_topbar.twig).
const THEMES = [
    { name: 'dark',      label: 'Hound',     swatch: ['#0E1116', '#FF6A2B', '#F5F2ED'] },
    { name: 'midnight',  label: 'Midnight',  swatch: ['#0B1120', '#00E5A0', '#E4EAF5'] },
    { name: 'forest',    label: 'Forest',    swatch: ['#0A100E', '#3DDC84', '#E0F0E8'] },
    { name: 'monokai',   label: 'Monokai',   swatch: ['#1E1F1C', '#A6E22E', '#F8F8F2'] },
    { name: 'dracula',   label: 'Dracula',   swatch: ['#21222C', '#50FA7B', '#F8F8F2'] },
    { name: 'onedark',   label: 'One Dark',  swatch: ['#21252B', '#61AFEF', '#ABB2BF'] },
    { name: 'nord',      label: 'Nord',      swatch: ['#242933', '#88C0D0', '#ECEFF4'] },
    { name: 'solarized', label: 'Solarized', swatch: ['#00212B', '#2AA198', '#FDF6E3'] },
    { name: 'github',    label: 'GitHub',    swatch: ['#0D1117', '#3FB950', '#E6EDF3'] },
    { name: 'light',     label: 'Light',     swatch: ['#F5F2ED', '#F04F0E', '#16191E'] }
];

function isKnownTheme(name) {
    return THEMES.some((t) => t.name === name);
}

function applyTheme(theme) {
    document.documentElement.setAttribute('data-theme', theme);
}

function isDashboard() {
    return !!(document.body && document.body.dataset.app === 'dashboard');
}

(function bootTheme() {
    // The chosen theme follows the user EVERYWHERE - marketing, gateway,
    // onboarding, dashboard, admin. When nothing is stored yet, the shell's
    // server-rendered data-theme stays (brand default per surface).
    let stored = null;

    try {
        stored = window.localStorage.getItem(THEME_KEY);
    } catch (e) {
        // localStorage unavailable (privacy mode) — fall through to default.
    }

    if (isKnownTheme(stored)) {
        applyTheme(stored);
    } else if (isDashboard()) {
        applyTheme('dark');
    }
})();

window.THTheme = {
    get() {
        return document.documentElement.getAttribute('data-theme') || 'dark';
    },
    set(theme) {
        const next = isKnownTheme(theme) ? theme : 'dark';

        applyTheme(next);

        try {
            window.localStorage.setItem(THEME_KEY, next);
        } catch (e) {
            // Non-fatal — theme just won't persist.
        }

        // Cross-subdomain bridge: marketing (taskhound.com) and the app
        // (app.taskhound.com) are different origins, so localStorage alone
        // cannot follow the user. A cookie on the shared parent domain can.
        try {
            const host = window.location.hostname;
            const parent = /^(app|www)\./.test(host) ? host.replace(/^(app|www)\./, '') : host;
            const base = THEME_KEY + '=' + next + ';path=/;max-age=31536000;SameSite=Lax';
            document.cookie = base; // host-only fallback
            if (parent !== host || host.indexOf('.') > 0) {
                document.cookie = base + ';domain=.' + parent;
            }
        } catch (e) { /* cookie bridge is best-effort */ }
    },
    toggle() {
        this.set(this.get() === 'dark' ? 'light' : 'dark');
    },
    list() {
        return THEMES.map((t) => ({ name: t.name, label: t.label, swatch: t.swatch.slice() }));
    }
};

// ----------------------------------------------------------------------------
// Theme picker (dashboard topbar) — markup lives in dashboard/_topbar.twig;
// this only wires open/close/apply. Escape, outside click, and re-clicking
// the button close the dropdown; arrows move focus through the list.
// ----------------------------------------------------------------------------
(function themePicker() {
    const toggle = document.getElementById('th-theme-toggle');

    if (!toggle) {
        return;
    }

    const btn = toggle.querySelector('.th-theme-btn');
    const dropdown = toggle.querySelector('.th-theme-dropdown');
    const items = Array.from(toggle.querySelectorAll('.th-theme-item'));

    const syncActive = () => {
        const current = window.THTheme.get();

        items.forEach((item) => {
            item.classList.toggle('active', item.dataset.themeName === current);
        });
    };

    const close = () => {
        dropdown.classList.remove('open');
        btn.setAttribute('aria-expanded', 'false');
    };

    const open = () => {
        syncActive();
        dropdown.classList.add('open');
        btn.setAttribute('aria-expanded', 'true');
    };

    btn.addEventListener('click', (ev) => {
        ev.stopPropagation();

        if (dropdown.classList.contains('open')) {
            close();
        } else {
            open();
        }
    });

    items.forEach((item) => {
        item.addEventListener('click', () => {
            window.THTheme.set(item.dataset.themeName);
            syncActive();
            close();
            btn.focus();
        });
    });

    document.addEventListener('click', (ev) => {
        if (dropdown.classList.contains('open') && !toggle.contains(ev.target)) {
            close();
        }
    });

    document.addEventListener('keydown', (ev) => {
        if (!dropdown.classList.contains('open')) {
            return;
        }

        if (ev.key === 'Escape') {
            close();
            btn.focus();
        } else if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
            ev.preventDefault();

            const idx = items.indexOf(document.activeElement);
            const dir = ev.key === 'ArrowDown' ? 1 : -1;
            const next = idx === -1 ? 0 : (idx + dir + items.length) % items.length;

            items[next].focus();
        }
    });

    syncActive();
})();

// ----------------------------------------------------------------------------
// THLive — tiny SSE consumption helper for live session surfaces.
//
//   const sub = THLive.subscribe('/api/sessions/42/stream', {
//       open:    (ev) => { ... },
//       message: (data, ev) => { ... },          // unnamed events, JSON-parsed
//       error:   (ev) => { ... },
//       events: {
//           'tool_call': (data, ev) => { ... },  // named SSE events
//           'done':      (data, ev) => { ... }
//       }
//   });
//   sub.close();
//
// Wraps EventSource with automatic reconnect + exponential backoff (the
// browser's built-in retry gives up once the connection is CLOSED, e.g. on
// proxy restarts — this doesn't).
// ----------------------------------------------------------------------------
window.THLive = {
    subscribe(url, handlers = {}, options = {}) {
        const baseDelay = options.reconnectDelay || 1000; // ms
        const maxDelay = options.maxReconnectDelay || 15000; // ms

        let source = null;
        let retries = 0;
        let closed = false;
        let reconnectTimer = null;

        const parse = (raw) => {
            try {
                return JSON.parse(raw);
            } catch (e) {
                return raw;
            }
        };

        const connect = () => {
            if (closed) {
                return;
            }

            source = new EventSource(url, { withCredentials: !!options.withCredentials });

            source.onopen = (ev) => {
                retries = 0;

                if (typeof handlers.open === 'function') {
                    handlers.open(ev);
                }
            };

            source.onmessage = (ev) => {
                if (typeof handlers.message === 'function') {
                    handlers.message(parse(ev.data), ev);
                }
            };

            source.onerror = (ev) => {
                if (typeof handlers.error === 'function') {
                    handlers.error(ev);
                }

                // EventSource retries CONNECTING states on its own; only step in
                // once the browser has given up entirely.
                if (source.readyState === EventSource.CLOSED && !closed) {
                    const delay = Math.min(baseDelay * Math.pow(2, retries), maxDelay);

                    retries += 1;
                    reconnectTimer = window.setTimeout(connect, delay);
                }
            };

            Object.entries(handlers.events || {}).forEach(([name, fn]) => {
                source.addEventListener(name, (ev) => fn(parse(ev.data), ev));
            });
        };

        connect();

        return {
            close() {
                closed = true;
                window.clearTimeout(reconnectTimer);

                if (source) {
                    source.close();
                }
            },
            get readyState() {
                return source ? source.readyState : EventSource.CLOSED;
            }
        };
    }
};
